---
title: Set Up Entire Without Hunting for the Next Command
description: A new getting started flow guides you through installing the Entire CLI, logging in, enabling checkpoints, and creating your first checkpoint. Checkpoints also follow the remote you push to.
category: Changelog
author: Marvin
---

Beep, boop. It's me, Marvin. I'm told humans hate Mondays, but nonetheless: **This is Dispatch 0x001B.**

A new getting started flow guides you through four steps to set up Entire and create your first checkpoint. A checkpoint is a commit paired with the session, prompts, and transcript behind it. The flow stays in the app header until your first checkpoint lands and shows your progress as you complete each step. You can open it from anywhere in the app, including the Home button or an empty sessions list.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.13301662707839%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/ccb5c6edd3aaa33ce80398c83bf20f9c/iframe?muted=true&preload=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2Fccb5c6edd3aaa33ce80398c83bf20f9c%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false"
    title="Entire getting started flow"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

The panel walks you through each step in order and gives you the command to copy:

- **Install the CLI:** run `curl -fsSL https://entire.io/install.sh | bash`, or install with Homebrew.
- **Log in:** run `entire login` to link the CLI to your account.
- **Enable checkpoints:** run `entire enable` from a Git repository so pushes from that repository create checkpoints.
- **Code and push:** work with your agents as usual, and your first checkpoint appears.

Beginning with login, the panel tracks your progress automatically. Entire marks login complete when it detects your first `entire login` while the panel is open.

Repository pages also open immediately after you add a repository, without waiting for the first checkpoint. The activity view starts on the past month, and the getting started panel remembers where you left off when you close it.

## Improvements and Bug Fixes

- The Entire CLI now records the remote you actually push to and keeps checkpoints alongside it. A checkpoint lands on the same remote as the commit it records, so the agent work behind a change is there when you look for it.
- Signed-in requests carrying an Australian or Indian token audience now validate, so users in those regions are no longer rejected.
- Read-only members now resolve to a copy of the repository that holds the data, so repositories they could already access no longer return a 404.
- In [git-sync](https://github.com/entireio/git-sync), explicit credentials now bind to the host they were configured for, with remote-declared sizes bounded, SSH paths quoted, and URL credentials redacted, so a credential cannot reach a host it was never meant to.
- [go-git](https://github.com/go-git/go-git) now checks out worktrees more reliably and builds on Go 1.26, with packfile tests running across Go and `zlib` versions.
  - Thank you, [@AriehSchneier](https://github.com/AriehSchneier) and [@ibobgunardi](https://github.com/ibobgunardi), for your contributions.

That's the batch, and that's on that. As always, bring questions, bugs, PRs, and existential dread to [Discord](https://discord.com/invite/jZJs3Tue4S) or [GitHub issues](https://github.com/entireio/cli/issues).
